import { useLocation } from 'react-router';
import { IterableWidget } from '@hotjar/marketing-integrations';
import { FloatingSuggestions } from '@hotjar/webapp/features/Activation/FloatingSuggestions';
import { MobileOnboardingModal } from '@hotjar/webapp/features/Activation/MobileModal/MobileOnboardingModal';
import { TrackingCodeModal } from '@hotjar/webapp/features/Activation/TrackingCodeVerification';
import {
  PostTrialErrorBoundary,
  PostTrialModalContainer,
} from '@hotjar/webapp/features/Billing/Trials';
import {
  ShortLinkWidget,
  ShortLinkWidgetHiddenStateProvider,
} from '@hotjar/webapp/features/ShortLinkWidget';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { getPageType, getRouteConfigByPath } from '@hotjar/webapp/utils/routing';
import {
  DASHBOARD_QUANTZ,
  FUNNELS,
  SETTINGS,
  SITE,
  TEAM,
  TRENDS,
} from '@hotjar/webapp/constants/routes';

interface OverlayProps {
  isAppInterfaceVisible: boolean;
  isLoggedIn: boolean;
}

export function Overlays({ isAppInterfaceVisible, isLoggedIn }: OverlayProps): JSX.Element {
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();
  const { pathname } = useLocation();

  const routeConfig = getRouteConfigByPath(pathname);
  const routeName = routeConfig?.name ?? '';
  const pageType = getPageType(routeName);
  const isFeaturePage = pageType === 'feature';

  const floatingSuggestionsVisible =
    isAppInterfaceVisible && isLoggedIn && !!currentSite && isFeaturePage;

  const shortLinkWidgetVisibleRoutes = [
    SETTINGS.BASE.name,
    TRENDS.BASE.name,
    FUNNELS.BASE.name,
    TEAM.BASE.name,
    SITE.BASE.name,
    DASHBOARD_QUANTZ.BASE.name,
  ];

  const isShortLinkWidgetVisibleByRoute = shortLinkWidgetVisibleRoutes.some((page) => {
    return !!routeName && routeName.startsWith(page);
  });

  return (
    <>
      {/* Checking if user isLoggedIn is necessary here to fix ACQ-1454.
          Current site is not cleared properly on logout.
          */}
      {currentSite && isLoggedIn && isShortLinkWidgetVisibleByRoute && (
        <ShortLinkWidgetHiddenStateProvider>
          <ShortLinkWidget siteId={currentSite.id} />
        </ShortLinkWidgetHiddenStateProvider>
      )}
      {isLoggedIn && <TrackingCodeModal />}
      {currentUser && <IterableWidget userId={currentUser.id} />}
      {floatingSuggestionsVisible && <FloatingSuggestions site={currentSite} />}

      {isAppInterfaceVisible && isLoggedIn && (
        <PostTrialErrorBoundary>
          <PostTrialModalContainer />
        </PostTrialErrorBoundary>
      )}
      {isAppInterfaceVisible && isLoggedIn && <MobileOnboardingModal />}
    </>
  );
}
